import { CopyIcon } from '@moia-dev/pace-icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MoiaButtonProps, UnstyledButtonProps } from './MoiaButton'
import { MoiaButton, UnstyledButton } from './MoiaButton'

type Props = {
  content: string
  message?: string
  children?: ReactNode
  onClick?: VoidFunction
} & MoiaButtonProps

const CopyToClipboardTooltip = ({
  message,
  children,
  open,
}: {
  children: ReactNode
  message?: string
  open: boolean
}) => {
  const { t } = useTranslation()
  return (
    <Tooltip placement="top" open={open}>
      <TooltipTrigger>
        <span>{children}</span>
      </TooltipTrigger>
      <TooltipContent css={theme => ({ zIndex: theme.zIndex.tooltip })}>
        {message ?? t('patterns_tooltip_copyToClipboardSuccess')}
      </TooltipContent>
    </Tooltip>
  )
}

const useCopyToClipboardButton = ({ content }: Pick<Props, 'content'>) => {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    let timeoutId = 0
    if (open) {
      //@ts-expect-error node js timeout in web
      timeoutId = setTimeout(() => {
        setOpen(false)
      }, 1500)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (open) {
        setOpen(false)
      }
    }
  }, [open])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content)
    setOpen(true)
  }
  return {
    handleCopy,
    open,
  }
}

export const CopyToClipboardButton = ({
  content,
  message,
  children,
  onClick,
  ...props
}: Props) => {
  const { handleCopy, open } = useCopyToClipboardButton({ content })

  return (
    <CopyToClipboardTooltip message={message} open={open}>
      <MoiaButton
        type="button"
        {...props}
        onClick={() => {
          handleCopy()
          onClick?.()
        }}
      >
        {children}
      </MoiaButton>
    </CopyToClipboardTooltip>
  )
}

export const CopyToClipboardIconButton = ({
  content,
  message,
  onClick,
  children,
  variant,
  ...props
}: UnstyledButtonProps & { variant?: 'inline' | 'wholeText' } & Pick<
    Props,
    'message' | 'content'
  >) => {
  const { handleCopy, open } = useCopyToClipboardButton({ content })
  return (
    <CopyToClipboardTooltip message={message} open={open}>
      <UnstyledButton
        {...props}
        css={{
          '& span': {
            display: 'grid',
            height: '100%',
            padding: '5px',
            marginLeft: '2px',
            alignContent: 'center',
          },
          borderRadius: '2px',
          display: 'inline-grid',
          svg: {
            color: 'var(--color-content-reduced)',
          },
          '&:hover svg': {
            color: 'var(--color-content-default)',
          },
          '&:hover': {
            background: 'var(--color-accessory-default-alternative)',
            '& span': {
              background: 'var(--color-accessory-default)',
              borderTopRightRadius: '2px',
              borderBottomRightRadius: '2px',
            },
          },
          ...(variant === 'wholeText' && {
            display: 'grid',
            alignItems: 'end',
            gridTemplateColumns: '1fr min-content',
            '& span': {
              display: 'grid',
              height: '100%',
              padding: '5px',
              marginLeft: '2px',
              alignContent: 'end',
            },
            justifyContent: 'start',
            textAlign: 'left',
            borderBottom: `solid 1px var(--color-border-on-control-default)`,
          }),
          ...(variant === 'inline' && {
            alignItems: 'center',
            gridTemplateColumns: '1fr min-content',
            alignContent: 'center',
            borderBottom: `solid 1px var(--color-border-on-control-default)`,
          }),
        }}
        onClick={e => {
          handleCopy()
          onClick?.(e)
        }}
      >
        {children}
        <span>
          <CopyIcon css={{ fontSize: '14px' }} />
        </span>
      </UnstyledButton>
    </CopyToClipboardTooltip>
  )
}
